import React, { useEffect, useState } from "react";
import moment from "moment";
import { Box, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import {
  Checkbox,
  CircularProgress,
  CustomPagination,
  DateRangePicker,
} from "../../components";
import HistoryCard from "./partials/HistoryCard";
import CountrySelect from "./partials/CountrySelect";
import { ShowAllFilter } from "./partials/ShowAllFilter";
import { ReactComponent as EmptyIcon } from "../../assets/empty-icon.svg";

import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { BORDER_COLORS, PLAN_LIMIT_EXCEEDED_ERR, TCountryCode } from "../../constants";
import { getLookupsThunk } from "../../features/lookups/lookups.thunk";
import { PER_PAGE_OPTIONS } from "../../constants/history";
import { selectLookups } from "../../features/lookups/lookups.selector";
import { IGetLookupsRequest } from "../../services/apiV2.types";
import { ILookup } from "../../features/lookups/lookups.types";
import { AxiosError } from "axios";
import { TErrorResponse } from "../../features/productAnalyzer/productAnalyzer.types";
import { useNavigate } from "react-router-dom";

const History: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up("md"));

  const [startDate, setStartDate] = useState<Date>(
    moment().subtract(1, "month").toDate(),
  );
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [isShowAll, setIsShowAll] = useState<boolean>(false);
  const [isFavorite, setIsFavorite] = useState<boolean>(false);
  const [isPurchased, setIsPurchased] = useState<boolean>(false);
  const [isReplenished, setIsReplenished] = useState<boolean>(false);
  const [countryCode, setCountryCode] = useState<TCountryCode>("US");
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(PER_PAGE_OPTIONS[1]);

  const { total, lookups, loadingLookups } = useAppSelector(selectLookups);

  const fetchLookups = (page: number, perPage: number) => {
    let params: IGetLookupsRequest = {
      startDate,
      endDate,
      page,
      perPage,
      countryCode,
    };
    if (isFavorite) params.isFavorite = true;
    if (isPurchased) params.isPurchased = true;
    if (isReplenished) params.isReplenished = true;

    dispatch(getLookupsThunk(params))
      .unwrap()
      .catch((e) => {
        const error = e as AxiosError<TErrorResponse>;
        if (error.status === 403) {
          if (
            error.response?.data?.ErrorMessage?.includes(
              PLAN_LIMIT_EXCEEDED_ERR,
            )
          ) {
            navigate("/account/subscriptions");
          }
        }
      });
  };

  useEffect(() => {
    setPage(1);
    fetchLookups(1, perPage);
  }, [startDate, endDate, isFavorite, isPurchased, isReplenished, countryCode]);

  const handlePageChange = (value: number) => {
    setPage(value);
    fetchLookups(value, perPage);
  };

  const handlePerPageChange = (value: number) => {
    setPage(1);
    setPerPage(value);
    fetchLookups(1, value);
  };

  return (
    <Box display="flex" flexDirection="column">
      <Stack
        gap="8px"
        flexDirection={{ xs: "column", sm: "row" }}
        flexWrap="wrap"
        alignItems="stretch"
        marginBottom="8px"
      >
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onChange={(startDate, endDate) => {
            setStartDate(startDate);
            setEndDate(endDate);
          }}
          sx={{
            height: "40px",
            borderColor: BORDER_COLORS.main,
          }}
        />

        <Stack
          sx={{
            height: "40px",
            display: "flex",
            alignItems: "center",
            justifyContent: { xs: "space-evenly", sm: "center" },
            flexDirection: "row",
            flexWrap: "nowrap",
            border: `1px solid ${BORDER_COLORS.main}`,
            borderRadius: "4px",
            padding: "4px 8px",
            gap: { xs: "4px", sm: "16px" },
            order: { xs: 2, sm: 0 },
          }}
        >
          <Checkbox
            label="Favorite"
            checked={isFavorite}
            onChange={(_e, checked) => setIsFavorite(checked)}
          />
          <Checkbox
            label="Purchased"
            checked={isPurchased}
            onChange={(_e, checked) => setIsPurchased(checked)}
          />
          <Checkbox
            label="Replenished"
            checked={isReplenished}
            onChange={(_e, checked) => setIsReplenished(checked)}
          />
        </Stack>

        <Box
          display={{ xs: "grid", sm: "flex" }}
          // gridTemplateColumns={{ xs: "1fr 1fr" }}
          flexWrap="wrap"
          gap="8px"
          sx={{ order: { xs: 1, sm: 0 } }}
        >
          {/* <CountrySelect code={countryCode} onChange={setCountryCode} /> */}

          {!mdUp && (
            <ShowAllFilter isShowAll={isShowAll} onChange={setIsShowAll} />
          )}
        </Box>
      </Stack>

      {/* List */}
      {loadingLookups ? (
        <Box
          sx={{
            height: "60vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size="lg" />
        </Box>
      ) : lookups.length ? (
        <>
          <Box
            display="flex"
            flexDirection="column"
            border={`1px solid ${BORDER_COLORS.main}`}
            borderRadius="4px"
          >
            {lookups.map((item: ILookup, index: number) => (
              <HistoryCard
                key={item.lookupId}
                item={item}
                isShowAll={isShowAll}
              />
            ))}
          </Box>
          <CustomPagination
            page={page}
            perPage={perPage}
            total={total}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            perPageOptions={PER_PAGE_OPTIONS}
          />
        </>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "60vh",
          }}
        >
          <EmptyIcon />
          <Typography variant="caption">No Result Found.</Typography>
        </Box>
      )}
    </Box>
  );
};

export default History;
